import React from "react";
import styled from "styled-components";
import { colors, typography } from "../styles/designSystem";

const DiagramContainer = styled.div`
    background: ${({ theme }) => theme.cardBackground || theme.background};
    border: 1px solid ${({ theme }) => theme.borderLight};
    border-radius: 12px;
    padding: 2rem;
    width: 100%;
    overflow-x: auto;
`;

const FlowWrapper = styled(DiagramContainer)`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0;
    padding: 2.5rem 2rem;
`;

const DiagramLabel = styled.span`
    font-size: ${typography.fontSize[100]};
    font-weight: ${typography.fontWeight.semibold};
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${({ theme }) => theme.subtleText};
`;

const Stage = styled.div`
    width: 100%;
    max-width: 520px;
    background: ${({ color }) => color}10;
    border: 2px solid ${({ color }) => color};
    border-radius: 8px;
    padding: 1.1rem 1.25rem;
    text-align: center;
`;

const StageTitle = styled.div`
    font-weight: ${typography.fontWeight.bold};
    font-size: ${typography.fontSize[200]};
    color: ${({ color }) => color};
    margin-bottom: 0.25rem;
`;

const StageDesc = styled.div`
    font-size: ${typography.fontSize[100]};
    color: ${({ theme }) => theme.subtleText};
    line-height: 1.5;
`;

const ChipRow = styled.div`
    display: flex;
    gap: 0.4rem;
    justify-content: center;
    flex-wrap: wrap;
    margin-top: 0.65rem;
`;

const Chip = styled.span`
    font-family: ${typography.fontFamily.code};
    font-size: ${typography.fontSize[50]};
    background: ${({ color }) => color}20;
    color: ${({ color }) => color};
    padding: 3px 10px;
    border-radius: 4px;
    white-space: nowrap;
`;

const FlowArrow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.45rem 0;
    color: ${({ theme }) => theme.subtleText};
    font-size: 20px;
    line-height: 1;
`;

export const RecoveryEngineDiagram = () => (
    <FlowWrapper>
        <DiagramLabel>System Architecture</DiagramLabel>
        <div style={{ height: "1.25rem" }} />

        <Stage color={colors.cyan[500]}>
            <StageTitle color={colors.cyan[500]}>Theft Trigger</StageTitle>
            <StageDesc>User reports the bike stolen from the mobile app</StageDesc>
        </Stage>

        <FlowArrow>&#8595;</FlowArrow>

        <Stage color={colors.indigo[500]}>
            <StageTitle color={colors.indigo[500]}>Match Search Orchestrator</StageTitle>
            <StageDesc>
                Backend job kicks off a parallel search using the bike's
                make, model, color, and reference photos
            </StageDesc>
        </Stage>

        <FlowArrow>&#8595;</FlowArrow>

        <Stage color={colors.amber[500]}>
            <StageTitle color={colors.amber[500]}>Marketplace Scrapers</StageTitle>
            <StageDesc>Parallel candidates pulled from public resale listings</StageDesc>
            <ChipRow>
                <Chip color={colors.amber[600]}>Facebook Marketplace</Chip>
                <Chip color={colors.amber[600]}>OfferUp</Chip>
                <Chip color={colors.amber[600]}>Craigslist</Chip>
                <Chip color={colors.amber[600]}>eBay</Chip>
            </ChipRow>
        </Stage>

        <FlowArrow>&#8595;</FlowArrow>

        <Stage color={colors.purple[500]}>
            <StageTitle color={colors.purple[500]}>Claude Vision Compare</StageTitle>
            <StageDesc>
                Each candidate listing photo is compared against the owner's
                reference photos for visual match
            </StageDesc>
        </Stage>

        <FlowArrow>&#8595;</FlowArrow>

        <Stage color={colors.emerald[500]}>
            <StageTitle color={colors.emerald[500]}>Probability-Scored Ranking</StageTitle>
            <StageDesc>
                Candidates returned with a confidence score and short reasoning;
                low-confidence noise filtered out
            </StageDesc>
        </Stage>

        <FlowArrow>&#8595;</FlowArrow>

        <Stage color={colors.coral[500]}>
            <StageTitle color={colors.coral[500]}>Twilio SMS &#8594; User Decision</StageTitle>
            <StageDesc>
                Top hits delivered as a text message with a link to the listing —
                the user makes the final call
            </StageDesc>
        </Stage>
    </FlowWrapper>
);

export default RecoveryEngineDiagram;
